import { HOLDING_PERIOD_MAX } from './scenarioNumbers'

// Purchase Date / sale date: the formula core reads only an ISO 'YYYY-MM-DD' prefix (reilm-be
// flip/dateMath.js parseLocalDate) and treats anything else as "no date" -- silently zero tax and
// interest. The scenario therefore stores ISO only; the box shows the design's M/D/YY.
// String arithmetic and UTC dates only, never local time: nothing here can shift a day with the timezone.
// The one local read is defaultPurchaseDate's day of `created` (or today), meant to be the user's own date.
// See README.md ("Purchase Date and Timeline are ISO dates").
export const SCENARIO_DATE_FIELDS = new Set(['purchase_date', 'sales_date'])

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T[\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/
const US_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/

const pad2 = (n) => String(n).padStart(2, '0')

function daysInMonth(year, month) {
  if (month === 2) return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

// Year floor 1000: the core builds `new Date(year, ...)`, which maps a year below 100 to 19xx.
function toIso(year, month, day) {
  if (year < 1000 || year > 9999) return null
  if (month < 1 || month > 12) return null
  if (day < 1 || day > daysInMonth(year, month)) return null
  return `${year}-${pad2(month)}-${pad2(day)}`
}

export function isBlankDate(raw) {
  return raw == null || (typeof raw === 'string' && raw.trim() === '')
}

// Typed or stored text -> 'YYYY-MM-DD', or null when blank, impossible or unrecognised.
// Accepts the design's M/D/YY (two-digit year = 20YY), M/D/YYYY, and ISO -- a stored ISO timestamp
// keeps its literal date part, as the backend's own prefix match does.
export function toIsoDate(raw) {
  if (typeof raw !== 'string') return null
  const text = raw.trim()
  let m = ISO_RE.exec(text)
  if (m) return toIso(Number(m[1]), Number(m[2]), Number(m[3]))
  m = US_RE.exec(text)
  if (m) {
    const year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])
    return toIso(year, Number(m[1]), Number(m[2]))
  }
  return null
}

// Stored value -> the box's text: M/D/YY, no leading zeros (sow3-section-lean.27.09.svg, `11/1/26`,
// `3/2/27`). A year outside 2000-2099 keeps all four digits: a typed two-digit year means 20YY, so
// showing 1999 as '99' would turn it into 2099 on the next edit. A value that does not parse is shown
// as it is, so it is never silently lost.
export function formatScenarioDate(value) {
  if (isBlankDate(value)) return ''
  const iso = toIsoDate(String(value))
  if (!iso) return String(value)
  const [year, month, day] = iso.split('-')
  const shownYear = year.startsWith('20') ? year.slice(-2) : year
  return `${Number(month)}/${Number(day)}/${shownYear}`
}

// A loaded property or imported template: any recognised format becomes ISO; blank and
// unrecognised values are left as they are (the box flags the latter, the payload sends null).
export function normalizeScenarioDates(scenario) {
  const next = { ...scenario }
  for (const key of SCENARIO_DATE_FIELDS) {
    if (!(key in next) || isBlankDate(next[key])) continue
    const iso = toIsoDate(String(next[key]))
    if (iso) next[key] = iso
  }
  return next
}

// Timeline (Cong, letter 89 item 5d; workbook Q19 = Q18 + P19 * 30.4): the user types months and the
// sale date is purchase + months x 30.4 days, the fraction dropped as the sheet's date cell shows it
// (11/1/26 + 4.0 -> 3/2/27, the design's own pair). Blank, unparseable or under one day: no sale date.
export const DAYS_PER_TIMELINE_MONTH = 30.4

export function saleDateFromMonths(purchaseDate, months) {
  const purchase = toIsoDate(String(purchaseDate ?? ''))
  const n = typeof months === 'number' ? months : Number.NaN
  if (!purchase || !Number.isFinite(n)) return null
  const days = Math.floor(n * DAYS_PER_TIMELINE_MONTH)
  if (days < 1) return null
  const [year, month, day] = purchase.split('-').map(Number)
  const sale = new Date(Date.UTC(year, month - 1, day + days))
  return toIso(sale.getUTCFullYear(), sale.getUTCMonth() + 1, sale.getUTCDate())
}

// Timeline mode (letter 122 item 3), stored in toggles.sale_date: only 'Actual' is Actual; 'Estimate'
// (legacy's Projected), '' and unset are Projected. README.md ("Timeline: Projected | Actual").
export const TIMELINE_PROJECTED = 'Estimate'
export const TIMELINE_ACTUAL = 'Actual'

export function isActualTimeline(scenario) {
  return scenario?.toggles?.sale_date === TIMELINE_ACTUAL
}

// Actual: the inverse of saleDateFromMonths -- days / 30.4, to the nearest tenth (Cong, letter 122,
// 3.1), so 11/1/24 -> 3/8/25 (127 days) is 4.2. Integer arithmetic (days * 100 / 304) so a half
// tenth rounds up, never down through float error. Null without two dates or with the sale on or
// before the purchase; a 1-2 day span is 0 (Cong's tenth rule applied literally: 0.03 -> 0).
export function monthsBetween(purchaseDate, saleDate) {
  const purchase = toIsoDate(String(purchaseDate ?? ''))
  const sale = toIsoDate(String(saleDate ?? ''))
  if (!purchase || !sale) return null
  const utc = (iso) => {
    const [year, month, day] = iso.split('-').map(Number)
    return Date.UTC(year, month - 1, day)
  }
  const days = Math.round((utc(sale) - utc(purchase)) / 86400000)
  if (days < 1) return null
  return Math.min(HOLDING_PERIOD_MAX, Math.round((days * 100) / 304) / 10)
}

// The one place the Timeline pair is kept consistent: load, import and every edit of Purchase Date,
// the months or the sale date. Projected: sales_date from the months. Actual: holding_period from the
// two dates; without both, the stored months are left as they are (so switching back loses nothing).
export function withDerivedTimeline(scenario) {
  if (isActualTimeline(scenario)) {
    const months = monthsBetween(scenario?.purchase_date, scenario?.sales_date)
    return months == null ? { ...scenario } : { ...scenario, holding_period: months }
  }
  return { ...scenario, sales_date: saleDateFromMonths(scenario?.purchase_date, scenario?.holding_period) }
}

// What the compute reads in Actual with no span (sale date blank, or on/before the purchase): no
// Timeline, as the backend already treats that sale date (parseDealDates). The stored months stay for
// a switch back to Projected. README.md ("Timeline: Projected | Actual").
export function withTimelineForCompute(scenario) {
  if (!isActualTimeline(scenario)) return scenario
  if (monthsBetween(scenario?.purchase_date, scenario?.sales_date) != null) return scenario
  return { ...scenario, holding_period: null }
}

// A switch keeps what is on screen: Projected -> Actual starts the editable sale date from the one shown
// (default purchase date included); Actual -> Projected keeps the shown, rounded months.
export function switchTimelineMode(scenario, mode, defaultIso = null) {
  const shown = withPlaceholderPurchaseDate(scenario, defaultIso)
  const toggles = { ...(scenario?.toggles ?? {}), sale_date: mode }
  if (mode === TIMELINE_ACTUAL) {
    return withDerivedTimeline({ ...scenario, toggles, sales_date: shown.sales_date ?? scenario?.sales_date ?? null })
  }
  return withDerivedTimeline({ ...scenario, toggles, holding_period: shown.holding_period ?? scenario?.holding_period ?? null })
}

// Purchase Date left blank: the scenario's server-stamped `created` + 14 days, fixed (letter 145 item 2;
// it was today + 14, letter 104). The day is the browser's local calendar day of that instant, never
// toISOString()'s UTC one; the +14 is done on that date in UTC. No usable `created` (the page's
// pre-scenario defaults, an odd legacy value): today + 14. README.md ("Purchase Date default").
export const DEFAULT_PURCHASE_DAYS = 14

export function defaultPurchaseDate(created, now = new Date()) {
  const text = typeof created === 'string' ? created.trim() : ''
  // A bare date would parse as UTC midnight (the day before, west of UTC); read it as a local day.
  const stamp = text === '' ? null : new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T00:00` : text)
  const fromCreated = stamp && Number.isFinite(stamp.getTime()) ? localDayPlus(stamp, DEFAULT_PURCHASE_DAYS) : null
  return fromCreated ?? localDayPlus(now, DEFAULT_PURCHASE_DAYS)
}

// Date.UTC maps a year 0-99 to 19xx, so a year toIso would refuse is refused before it.
function localDayPlus(instant, days) {
  const year = instant.getFullYear()
  if (year < 1000 || year > 9999) return null
  const date = new Date(Date.UTC(year, instant.getMonth(), instant.getDate() + days))
  return toIso(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate())
}

// What the compute and the derived sale date read: the scenario as stored, or with the default
// purchase date while none is stored. Never written back -- README.md ("Purchase Date default").
export function withPlaceholderPurchaseDate(scenario, defaultIso) {
  if (!defaultIso || !isBlankDate(scenario?.purchase_date)) return scenario
  return withDerivedTimeline({ ...scenario, purchase_date: defaultIso })
}
